/**
 * Pure H OS · Sprint 3 · Content Pipeline (Kanban).
 * Composes Card, SectionHeader.
 */
import { Sparkles } from "lucide-react";
import { Card } from "../../components/lib";
import { CONTENT, ContentStage } from "../../mocks/marketingData";

const COLUMNS: { key: ContentStage; label: string }[] = [
  { key: "idea",      label: "Idea"      },
  { key: "draft",     label: "Draft"     },
  { key: "review",    label: "Review"    },
  { key: "scheduled", label: "Scheduled" },
  { key: "published", label: "Published" },
];

const TYPE_LABEL: Record<string, string> = {
  post: "Post", reel: "Reel", video: "Video", story: "Story", email: "Email", article: "Article",
};

export default function Pipeline() {
  const grouped: Record<ContentStage, typeof CONTENT> = {
    idea: [], draft: [], review: [], scheduled: [], published: [],
  };
  CONTENT.forEach((c) => grouped[c.stage].push(c));

  return (
    <div data-testid="mkt-pipeline">
      <div
        className="grid gap-4 overflow-x-auto pb-1"
        style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(240px, 1fr))` }}
      >
        {COLUMNS.map((col) => (
          <div key={col.key} data-testid={`mkt-pipe-col-${col.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--ph-ink-muted)" }}>
                {col.label}
              </div>
              <span
                className="inline-flex items-center justify-center text-[10px] font-semibold"
                style={{
                  minWidth: 20, height: 16, padding: "0 6px",
                  background: "var(--ph-neutral-tint)", color: "var(--ph-ink-muted)",
                  borderRadius: "999px",
                }}
              >
                {grouped[col.key].length}
              </span>
            </div>

            <div className="space-y-2">
              {grouped[col.key].length === 0 ? (
                <div
                  className="py-6 text-center text-xs"
                  style={{
                    color: "var(--ph-ink-soft)",
                    border: "1px dashed var(--ph-border-subtle)",
                    borderRadius: "var(--ph-radius-md)",
                  }}
                >
                  Nothing here.
                </div>
              ) : grouped[col.key].map((item) => (
                <Card key={item.id} testId={`mkt-content-${item.id}`}>
                  <div className="flex items-center gap-3">
                    <div
                      style={{
                        width: 34, height: 34, flexShrink: 0,
                        borderRadius: "var(--ph-radius-sm)",
                        background: item.thumb,
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>
                        {TYPE_LABEL[item.type]} · {item.channel}
                      </div>
                      <div className="text-sm font-semibold mt-0.5 line-clamp-2" style={{ color: "var(--ph-ink-strong)" }}>
                        {item.title}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>
                    <span>{item.owner}{item.scheduledFor ? ` · ${item.scheduledFor}` : ""}</span>
                    {item.aiAssisted && (
                      <span className="inline-flex items-center gap-1" style={{ color: "var(--ph-ai)" }}>
                        <Sparkles size={10} /> AI-assisted
                      </span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
